import { useMutation } from "@tanstack/react-query";
import { FieldValues } from "react-hook-form";
import { generateCsv, asBlob, mkConfig } from "export-to-csv";

import { fetchAuthenticated, fetchAuthenticatedData } from "@/queries/shared";
import { GlobalStateModel, MutationResponse } from "@/models";

const SERVER_ERROR_MESSAGE =
  "Couldn't save your changes — the server returned an error. Please try again, or contact support if it continues.";

export function buildRecordMutation<T extends FieldValues>(
  leaContext: GlobalStateModel,
  setSubmitResult: (resp: MutationResponse<T>) => void,
  serialize: (record: T) => unknown,
  editPath: string
) {
  const mutation = useMutation({
    mutationFn: ({ urlVal, record }: { urlVal: string; record: T }) => {
      return fetchAuthenticatedData<MutationResponse<T>>(leaContext, urlVal, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(serialize(record)),
      }).then((resp) => {
        setSubmitResult(resp);
      });
    },
    onError: (error) => {
      console.error(error);
      setSubmitResult({
        fileSubmission: null,
        errors: [],
        serverError: SERVER_ERROR_MESSAGE,
      });
    },
  });
  return {
    mutationState: mutation,
    handleSave: async function (
      record: T,
      isDelete: boolean = false,
      transactionTypeOverride?: string
    ) {
      const transactionType = transactionTypeOverride ?? (isDelete ? "D" : "U");
      const urlVal = `/${leaContext.currentUserOrg!.esiIdPath}/dashboard/${transactionType}/ajax/${editPath}`;
      mutation.mutate({ urlVal, record });
    },
  };
}

// Some record types don't have a dedicated Edit{RecordType}Ajax endpoint yet. This builds a
// single-row CSV for the record and submits it through the existing generic file-upload
// endpoint instead, so a record can be added without a new backend action. Once a dedicated
// endpoint exists, callers should switch to buildRecordMutation like every other record type.
export function buildFileUploadMutation<T extends FieldValues>(
  leaContext: GlobalStateModel,
  setSubmitResult: (resp: MutationResponse<T>) => void,
  serialize: (record: T) => Record<string, string>,
  recordType: string,
  columnHeaders: string[],
  buildJobName: (record: T) => string
) {
  const csvConfig = mkConfig({
    fieldSeparator: ",",
    filename: recordType.toLowerCase(),
    decimalSeparator: ".",
    showColumnHeaders: false,
    columnHeaders,
  });

  const mutation = useMutation({
    mutationFn: async ({
      record,
      transactionType,
    }: {
      record: T;
      transactionType: string;
    }) => {
      const row = {
        RecordTypeCode: recordType,
        TransactionTypeCode: transactionType,
        ...serialize(record),
      };
      const csv = generateCsv(csvConfig)([row]);
      const blob = asBlob(csvConfig)(csv);

      const formData = new FormData();
      formData.append("csvFile", blob, buildJobName(record));
      formData.append("recordType", recordType);
      formData.append("isHeaderRow", "false");

      const resp = await fetchAuthenticated(
        leaContext,
        `/${leaContext.currentUserOrg!.esiIdPath}/files/ajax/UploadRecordsAjax`,
        {
          method: "POST",
          headers: { Accept: "application/json" },
          body: formData,
        }
      );
      const result = (await resp.json()) as MutationResponse<T>;
      setSubmitResult(result);
    },
    onError: (error) => {
      console.error(error);
      setSubmitResult({
        fileSubmission: null,
        errors: [],
        serverError: SERVER_ERROR_MESSAGE,
      });
    },
  });

  return {
    mutationState: mutation,
    handleSave: async function (
      record: T,
      isDelete: boolean = false,
      transactionTypeOverride?: string
    ) {
      const transactionType = transactionTypeOverride ?? (isDelete ? "D" : "U");
      mutation.mutate({ record, transactionType });
    },
  };
}
